<script setup>
// CONECTAR / EDITAR A OPERADORA (telefonia, etapa D, 25/09/2026) — para quem
// instala: os dados SIP que o provedor passa. Dois modos: por REGISTRO (usuário
// e senha, o comum no Brasil) ou por IP (a operadora reconhece o endereço do
// PBX). A senha nunca volta da API; em branco na edição mantém a atual.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import AdvupSelect from 'dashboard/components-next/AdvupSelect.vue';
import { opcoesDeDestino as destinos } from './roteamento/destinos';

const props = defineProps({
  operadora: { type: Object, default: null },
  ramais: { type: Array, default: () => [] },
  // ramais + filas/URAs/horários (etapa E): o padrão pode ser qualquer destino
  contexto: { type: Object, default: null },
  salvando: { type: Boolean, default: false },
  erro: { type: String, default: '' },
});
const emit = defineEmits(['close', 'salvar']);

const { t } = useI18n();
const T = 'TELEFONIA.OPERADORA.MODAL';
const editando = computed(() => Boolean(props.operadora));
const o = props.operadora || {};

const nome = ref(o.nome || '');
const host = ref(o.host || '');
const porta = ref(String(o.porta || 5060));
const modo = ref(o.modo || 'registro');
const usuario = ref(o.usuario || '');
const senha = ref('');
const dominio = ref(o.dominio || '');
const bina = ref(o.bina || '');
const ddd = ref(o.ddd || '27');
const formato = ref(o.formato || 'nacional');
const destinoPadrao = ref(o.destino_padrao || '');
const gravar = ref(editando.value ? o.gravar : true);
const ativa = ref(editando.value ? o.ativo : true);
const tentou = ref(false);

const MODOS = ['registro', 'ip'];
const opcoesDeFormato = computed(() =>
  ['nacional', 'e164', 'zero'].map(v => ({
    value: v,
    label: t(`${T}.FORMATO_${v.toUpperCase()}`),
  }))
);
const opcoesDeDestino = computed(() =>
  destinos(
    props.contexto || {
      ramais: props.ramais,
      filas: [],
      uras: [],
      horarios: [],
    },
    t,
    { comVazio: t(`${T}.DESTINO_PRIMEIRO`) }
  )
);

// E.164 a partir do que a pessoa digitar: (27) 3333-4444 → +552733334444
const binaE164 = computed(() => {
  const d = bina.value.replace(/\D/g, '');
  if (!d) return '';
  if (d.startsWith('55') && d.length >= 12) return `+${d}`;
  return `+55${d.replace(/^0+/, '')}`;
});

const faltando = computed(() => {
  const erros = [];
  if (!nome.value.trim()) erros.push('nome');
  if (!/^[a-z0-9.-]+$/i.test(host.value.trim())) erros.push('host');
  if (modo.value === 'registro' && !usuario.value.trim()) erros.push('usuario');
  if (
    modo.value === 'registro' &&
    !senha.value &&
    !(editando.value && o.senha_configurada)
  )
    erros.push('senha');
  return erros;
});

function salvar() {
  tentou.value = true;
  if (faltando.value.length) return;
  emit('salvar', {
    name: nome.value.trim(),
    host: host.value.trim(),
    port: Number(porta.value) || 5060,
    auth_mode: modo.value,
    username: usuario.value.trim(),
    password: senha.value,
    from_domain: dominio.value.trim(),
    caller_id: binaE164.value,
    area_code: ddd.value.replace(/\D/g, '').slice(0, 2),
    dial_format: formato.value,
    default_destination: destinoPadrao.value,
    record_calls: gravar.value,
    enabled: ativa.value,
  });
}
const erroEm = campo => tentou.value && faltando.value.includes(campo);
</script>

<template>
  <div class="advup-modal-fundo" @click.self="emit('close')">
    <div
      v-modal-acessivel="() => emit('close')"
      class="advup-modal tel-operadora"
    >
      <div class="advup-modal__topo advup-modal__topo--teal">
        <span class="advup-modal__selo" aria-hidden="true">
          <i class="i-ph-broadcast-fill" />
        </span>
        <div class="min-w-0 flex-1">
          <h3 class="advup-modal__titulo">
            {{ editando ? t(`${T}.EDITAR`) : t(`${T}.NOVA`) }}
          </h3>
          <p class="advup-modal__sub">{{ t(`${T}.SUB`) }}</p>
        </div>
        <button
          type="button"
          class="advup-modal__fechar"
          :aria-label="t(`${T}.CANCELAR`)"
          @click="emit('close')"
        >
          <i class="i-lucide-x" />
        </button>
      </div>

      <form
        class="advup-modal__corpo tel-operadora__corpo"
        @submit.prevent="salvar"
      >
        <label class="tel-operadora__campo tel-operadora__campo--largo">
          <span class="advup-rotulo">{{ t(`${T}.NOME`) }}</span>
          <input
            v-model="nome"
            name="operadora-nome"
            type="text"
            maxlength="60"
            autocomplete="off"
            class="advup-campo reset-base"
            :class="{ 'advup-campo--erro': erroEm('nome') }"
            :placeholder="t(`${T}.NOME_PLACEHOLDER`)"
          />
        </label>

        <div class="tel-operadora__campo tel-operadora__campo--largo">
          <span class="advup-rotulo">{{ t(`${T}.MODO`) }}</span>
          <div class="advup-seg tel-operadora__seg" role="radiogroup">
            <button
              v-for="m in MODOS"
              :key="m"
              type="button"
              role="radio"
              class="advup-seg__btn"
              :class="{ 'advup-seg__btn--on': modo === m }"
              :aria-checked="modo === m"
              @click="modo = m"
            >
              {{ t(`${T}.MODO_${m.toUpperCase()}`) }}
            </button>
          </div>
          <span class="advup-nota">{{
            t(`${T}.MODO_DICA_${modo.toUpperCase()}`)
          }}</span>
        </div>

        <label class="tel-operadora__campo tel-operadora__campo--4">
          <span class="advup-rotulo">{{ t(`${T}.HOST`) }}</span>
          <input
            v-model="host"
            name="operadora-host"
            type="text"
            autocomplete="off"
            spellcheck="false"
            class="advup-campo reset-base"
            :class="{ 'advup-campo--erro': erroEm('host') }"
            :placeholder="t(`${T}.HOST_PLACEHOLDER`)"
          />
        </label>
        <label class="tel-operadora__campo tel-operadora__campo--2">
          <span class="advup-rotulo">{{ t(`${T}.PORTA`) }}</span>
          <input
            v-model="porta"
            name="operadora-porta"
            type="text"
            inputmode="numeric"
            maxlength="5"
            autocomplete="off"
            class="advup-campo reset-base"
          />
        </label>

        <template v-if="modo === 'registro'">
          <label class="tel-operadora__campo tel-operadora__campo--3">
            <span class="advup-rotulo">{{ t(`${T}.USUARIO`) }}</span>
            <input
              v-model="usuario"
              name="operadora-usuario"
              type="text"
              autocomplete="off"
              spellcheck="false"
              class="advup-campo reset-base"
              :class="{ 'advup-campo--erro': erroEm('usuario') }"
            />
          </label>
          <label class="tel-operadora__campo tel-operadora__campo--3">
            <span class="advup-rotulo">
              {{ t(`${T}.SENHA`) }}
              <span v-if="editando && o.senha_configurada" class="advup-nota">
                · {{ t(`${T}.SENHA_MANTEM`) }}
              </span>
            </span>
            <input
              v-model="senha"
              name="operadora-senha"
              type="password"
              autocomplete="new-password"
              data-1p-ignore
              data-lpignore="true"
              class="advup-campo reset-base"
              :class="{ 'advup-campo--erro': erroEm('senha') }"
            />
          </label>
        </template>

        <label class="tel-operadora__campo tel-operadora__campo--4">
          <span class="advup-rotulo">{{ t(`${T}.BINA`) }}</span>
          <input
            v-model="bina"
            name="operadora-bina"
            type="tel"
            inputmode="tel"
            autocomplete="off"
            class="advup-campo reset-base"
            :placeholder="t(`${T}.BINA_PLACEHOLDER`)"
          />
          <span class="advup-nota">{{ t(`${T}.BINA_DICA`) }}</span>
        </label>
        <label class="tel-operadora__campo tel-operadora__campo--2">
          <span class="advup-rotulo">{{ t(`${T}.DDD`) }}</span>
          <input
            v-model="ddd"
            name="operadora-ddd"
            type="text"
            inputmode="numeric"
            maxlength="2"
            autocomplete="off"
            class="advup-campo reset-base"
          />
        </label>

        <div class="tel-operadora__campo tel-operadora__campo--3">
          <span class="advup-rotulo">{{ t(`${T}.FORMATO`) }}</span>
          <AdvupSelect
            v-model="formato"
            :options="opcoesDeFormato"
            :rotulo="t(`${T}.FORMATO`)"
            min-width="100%"
          />
        </div>
        <div class="tel-operadora__campo tel-operadora__campo--3">
          <span class="advup-rotulo">{{ t(`${T}.DESTINO`) }}</span>
          <AdvupSelect
            v-model="destinoPadrao"
            :options="opcoesDeDestino"
            :rotulo="t(`${T}.DESTINO`)"
            min-width="100%"
          />
        </div>

        <label class="tel-operadora__campo tel-operadora__campo--largo">
          <span class="advup-rotulo">{{ t(`${T}.DOMINIO`) }}</span>
          <input
            v-model="dominio"
            name="operadora-dominio"
            type="text"
            autocomplete="off"
            spellcheck="false"
            class="advup-campo reset-base"
            :placeholder="host || t(`${T}.DOMINIO_PLACEHOLDER`)"
          />
          <span class="advup-nota">{{ t(`${T}.DOMINIO_DICA`) }}</span>
        </label>

        <button
          type="button"
          role="switch"
          class="advup-chave tel-operadora__campo--largo"
          :aria-checked="String(gravar)"
          @click="gravar = !gravar"
        >
          <span class="advup-chave__trilho" aria-hidden="true" />
          <span class="advup-chave__txt">
            <span class="advup-chave__nome">{{ t(`${T}.GRAVAR`) }}</span>
            <span class="advup-chave__sub">{{ t(`${T}.GRAVAR_DICA`) }}</span>
          </span>
        </button>
        <button
          v-if="editando"
          type="button"
          role="switch"
          class="advup-chave advup-chave--ambar tel-operadora__campo--largo"
          :aria-checked="String(ativa)"
          @click="ativa = !ativa"
        >
          <span class="advup-chave__trilho" aria-hidden="true" />
          <span class="advup-chave__txt">
            <span class="advup-chave__nome">{{ t(`${T}.ATIVA`) }}</span>
            <span class="advup-chave__sub">{{ t(`${T}.ATIVA_DICA`) }}</span>
          </span>
        </button>

        <p
          v-if="erro || (tentou && faltando.length)"
          class="tel-operadora__erro tel-operadora__campo--largo"
          role="alert"
        >
          <i class="i-ph-warning-octagon-fill" aria-hidden="true" />
          <span>{{ erro || t(`${T}.FALTANDO`) }}</span>
        </p>
      </form>

      <div class="advup-modal__rodape advup-modal__rodape--acoes">
        <button type="button" class="advup-btn--vazado" @click="emit('close')">
          {{ t(`${T}.CANCELAR`) }}
        </button>
        <button
          type="button"
          class="advup-btn-forte"
          :disabled="salvando"
          @click="salvar"
        >
          <i
            :class="
              salvando ? 'i-ph-circle-notch advup-gira' : 'i-ph-check-bold'
            "
            aria-hidden="true"
          />
          {{ salvando ? t(`${T}.SALVANDO`) : t(`${T}.SALVAR`) }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tel-operadora {
  width: min(640px, calc(100vw - 2 * var(--esp-4)));
}
.tel-operadora__corpo {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: var(--esp-4);
}
.tel-operadora__campo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-1);
  min-width: 0;
}
.tel-operadora__campo--largo {
  grid-column: 1 / -1;
}
.tel-operadora__campo--4 {
  grid-column: span 4;
}
.tel-operadora__campo--3 {
  grid-column: span 3;
}
.tel-operadora__campo--2 {
  grid-column: span 2;
}
.tel-operadora__seg {
  align-self: flex-start;
}
.tel-operadora__erro {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-2);
  margin: 0;
  padding: var(--esp-3);
  border-radius: var(--r-3);
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
  background: rgb(var(--ruby-3));
  color: rgb(var(--ruby-11));
}
@media (max-width: 639px) {
  .tel-operadora__campo--4,
  .tel-operadora__campo--3 {
    grid-column: 1 / -1;
  }
  .tel-operadora__campo--2 {
    grid-column: span 3;
  }
}
</style>
